import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Check, Clock, CheckCircle, ArrowUpRight, Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../App';
import { useAccessControl } from '../../context/AccessControlContext';
import userService from '../../services/userServices';
import billingServices from '../../services/billingServices';
import { showSuccessBanner, showErrorBanner } from '../../components/common/ErrorBannerManager';
import styles from './OnboardingBilling.module.css';
import FoundryLogo from '../../components/assets/Foundry_logo_dark_transparent_background.png';
import FoundryLogoLight from '../../components/assets/Foundry_logo_light_transparent_background.png';

const OnboardingBilling = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasVerified, setHasVerified] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { setIsAuthenticated } = useAuthContext();
  const { refresh: refreshAccessControl } = useAccessControl();
  
  useEffect(() => {
    // Check if returning from successful checkout
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    
    if (sessionId && !hasVerified) {
      setHasVerified(true);
      // User completed checkout - verify and sync subscription
      const verifySub = async () => {
        try {
          const result = await billingServices.verifyCheckoutSession(sessionId);
          
          // Refresh access control to update user permissions immediately
          await refreshAccessControl();
          
          if (result?.data?.synced) {
            showSuccessBanner({
              title: 'Trial Started!',
              message: 'Your 7-day free trial has started. Redirecting...',
              position: 'top-right'
            });
            // Wait a moment for the banner to show, then redirect to landing
            setTimeout(() => {
              navigate('/landing', { replace: true });
            }, 1500);
          } else {
            // Subscription already exists or synced via webhook
            navigate('/landing', { replace: true });
          }
        } catch (err) {
          console.error('Failed to verify checkout session:', err);
          // Still refresh access control - webhook may have already processed
          await refreshAccessControl();
          // Trial is being activated via webhook - show success message
          showSuccessBanner({
            title: 'Trial Activating',
            message: 'Your 7-day free trial is being activated. Redirecting...',
            position: 'top-right'
          });
          // Still redirect after a delay
          setTimeout(() => {
            navigate('/landing', { replace: true });
          }, 2000);
        }
      };
      verifySub();
      return; // Don't fetch plans if we're redirecting
    }
    
    if (cancelled) {
      // User cancelled checkout
      showErrorBanner({
        title: 'Checkout Cancelled',
        message: 'You can start your trial anytime by clicking the button below.',
        position: 'top-right'
      });
    }
    
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const fetchedPlans = await billingServices.getAvailablePlans();
        
        // If API fails, use fallback data that matches your pricing
        if (!Array.isArray(fetchedPlans) || fetchedPlans.length === 0) {
          const fallbackPlans = [
            {
              id: 'pro',
              name: 'Pro',
              price: 50.00,
              interval: 'month',
              features: ['Unlimited projects', 'Metered 2D/3D CAD servers', 'Metered Agent requests', 'Advanced usage analytics']
            },
            {
              id: 'business',
              name: 'Business',
              price: 200.00,
              interval: 'month',
              features: ['Everything in Pro', 'Extended limits on 2D/3D CAD servers', 'Extended limits on Agent requests', 'Advanced project analytics', 'Multi-user access', 'Productivity tools integrations']
            },
            {
              id: 'enterprise',
              name: 'Enterprise',
              price: 'Custom',
              interval: 'month',
              features: ['Everything in Business', '10x usage on 2D/3D CAD servers', '10x usage on Agent requests', 'On-premise installations', 'Custom integrations', 'Access control features', 'Priority support and account management']
            }
          ];
          setPlans(fallbackPlans);
        } else {
          setPlans(fetchedPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, [navigate, searchParams]);
  
  const handleStartTrial = async () => {
    try {
      setIsStartingTrial(true);
      // Create Stripe Checkout session for trial with payment method collection
      const result = await billingServices.createTrialCheckoutSession();
      
      // Check if result is an object with redirect (subscription was synced)
      if (result && typeof result === 'object' && result.subscription_synced) {
        showSuccessBanner({
          title: 'Subscription Found!',
          message: 'Your existing subscription has been synced. Redirecting...',
          position: 'top-right'
        });
        setTimeout(() => {
          navigate(result.redirect || '/landing', { replace: true });
        }, 1500);
        return;
      }
      
      // Otherwise, result is the checkout URL
      if (result) {
        window.location.href = result;
      } else {
        setError('Failed to create trial checkout session');
      }
    } catch (err) {
      console.error('Failed to start trial:', err);
      setError(err.message || 'Failed to start trial');
    } finally {
      setIsStartingTrial(false);
    }
  };
  
  const handleSelectPlan = async (planId) => {
    try {
      const checkoutUrl = await billingServices.createCheckoutSession(planId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
    }
  };
  
  const handleContactSales = () => {
    const subject = encodeURIComponent('Enterprise Plan Inquiry');
    const body = encodeURIComponent('Hi,\n\nI am interested in learning more about the Enterprise plan for Foundry. Please contact me to discuss pricing and features.\n\nThank you!');
    window.open(`mailto:support@siameseai.com?subject=${subject}&body=${body}`, '_blank');
  };
  
  const handleLogout = async () => {
    setIsAuthenticated(false);
    try {
      await userService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/login', { replace: true });
  };
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  return (
    <>
      {/* TaskBar */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <Link to="/landing" className={styles.logoLink}>
              <img 
                src={theme === 'dark' ? FoundryLogo : FoundryLogoLight} 
                alt="FOUNDRY" 
                className={styles.logoImage} 
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className={styles.nav}>
            {/* Theme Toggle Button */}
            <button 
              onClick={toggleTheme}
              className={styles.themeToggle}
              aria-label="Toggle theme"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            
            <button onClick={handleLogout} className={styles.logoutButton}>Log Out</button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className={styles.mobileMenu}>
            {/* Theme Toggle for Mobile */}
            <button 
              onClick={() => {
                toggleTheme();
                toggleMobileMenu();
              }}
              className={styles.mobileThemeToggle}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <>
                  <Sun size={18} />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon size={18} />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
            
            <button onClick={() => { handleLogout(); toggleMobileMenu(); }} className={styles.mobileLogoutButton}>
              Log Out
            </button>
          </div>
        )}
      </header>
    
      <div className={styles.onboardingPage}>
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>Welcome to SiameseAI Foundry!</h1>
          </div>
        
        {/* Trial Offer Card */}
        <div className={styles.trialCard}>
          <div className={styles.trialContent}>
            <div className={styles.trialLeft}>
              <div className={styles.trialHeader}>
                <Clock className={styles.trialIcon} size={28} />
                <h2 className={styles.trialTitle}>Start with a Free Trial</h2>
              </div>
              <ul className={styles.benefits}>
                <li><Check size={18} /> 7 days free access</li>
                <li><Check size={18} /> $25 in credits</li>
                <li><Check size={18} /> Full feature access</li>
                <li><Check size={18} /> No charge until trial ends</li>
              </ul>
            </div>
            <div className={styles.trialRight}>
              <button 
                className={styles.trialButton} 
                onClick={handleStartTrial}
                disabled={isStartingTrial}
              >
                {isStartingTrial ? 'Starting...' : 'Start Free Trial'}
              </button>
              <p className={styles.trialNote}>
                Credit card required • Cancel anytime • No charge during trial
              </p>
            </div>
          </div>
        </div>
        
        {/* Plans Section */}
        <div className={styles.plansSection}>
          <h2 className={styles.plansTitle}>Or Choose a Plan</h2>
          
          {loading && !plans.length ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading plans...</p>
            </div>
          ) : error && !plans.length ? (
            <div className={styles.errorState}>
              <p>Failed to load plans: {error}</p>
            </div>
          ) : (
            <div className={styles.planCards}>
              {plans.map((plan) => (
                <div key={plan.id} className={styles.planCard}>
                  <div className={styles.planHeader}>
                    <h3 className={styles.planName}>{plan.name}</h3>
                  <div className={styles.planPrice}>
                    {typeof plan.price === 'number' ? (
                      <>
                        <span className={styles.amount}>${plan.price}</span>
                        <span className={styles.interval}>/user/month</span>
                      </>
                    ) : (
                      <span className={styles.customPrice}>{plan.price}</span>
                    )}
                  </div>
                  </div>
                  
                  <ul className={styles.features}>
                    {plan.features && plan.features.map((feature, idx) => (
                      <li key={idx}>
                        <CheckCircle size={16} className={styles.checkIcon} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    className={plan.name === 'Enterprise' ? styles.contactButton : styles.selectButton}
                    onClick={() => plan.name === 'Enterprise' ? handleContactSales() : handleSelectPlan(plan.id)}
                  >
                    {plan.name === 'Enterprise' ? 'Contact Sales' : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default OnboardingBilling;
